(() => {
  const TOGGLE_SELECTOR = '[data-menu-toggle]';
  const PANEL_SELECTOR = '[data-menu-panel]';
  const DESKTOP_BREAKPOINT = 1100;
  let footerObserver = null;

  function getPanel(button) {
    const controlledId = button?.getAttribute('aria-controls');
    if (controlledId) {
      const controlledPanel = document.getElementById(controlledId);
      if (controlledPanel) return controlledPanel;
    }

    return button?.closest('ignor-menu')?.querySelector(PANEL_SELECTOR) || null;
  }

  function findButtonForPanel(panel) {
    if (!panel) return null;

    const panelId = panel.id;
    if (panelId) {
      const matchingButton = Array.from(document.querySelectorAll(TOGGLE_SELECTOR))
        .find(button => button.getAttribute('aria-controls') === panelId);
      if (matchingButton) return matchingButton;
    }

    return panel.closest('ignor-menu')?.querySelector(TOGGLE_SELECTOR) || null;
  }

  function hasOtherOpenOverlay() {
    return Boolean(document.querySelector('dialog[open], [data-filter-drawer]:not([hidden])'));
  }

  function syncPageLock() {
    const anyMenuOpen = Boolean(document.querySelector(`${TOGGLE_SELECTOR}[aria-expanded="true"]`));

    if (anyMenuOpen) {
      document.documentElement.classList.add('ignor-drawer-open');
    } else if (!hasOtherOpenOverlay()) {
      document.documentElement.classList.remove('ignor-drawer-open');
    }
  }

  function setMenuOpen(button, open, options = {}) {
    const panel = getPanel(button);
    if (!button || !panel) return false;

    button.setAttribute('aria-expanded', String(open));
    button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    panel.hidden = !open;
    panel.setAttribute('aria-hidden', String(!open));
    document.querySelector('[data-ignor-header]')?.classList.toggle('is-menu-open', open);
    syncPageLock();

    if (open && options.focusPanel !== false) {
      window.requestAnimationFrame(() => {
        panel.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus();
      });
    }

    if (!open && options.restoreFocus) {
      window.requestAnimationFrame(() => button.focus());
    }

    return true;
  }

  function closeAllMenus(options = {}) {
    document.querySelectorAll(`${TOGGLE_SELECTOR}[aria-expanded="true"]`).forEach(button => {
      setMenuOpen(button, false, options);
    });
  }

  function initializeMenus(root = document) {
    root.querySelectorAll?.(TOGGLE_SELECTOR).forEach(button => {
      const panel = getPanel(button);
      if (!panel) return;

      const isOpen = button.getAttribute('aria-expanded') === 'true';
      panel.hidden = !isOpen;
      panel.setAttribute('aria-hidden', String(!isOpen));
      button.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    });

    syncPageLock();
  }

  function organizeFooterPrivacyControls() {
    const footer = document.querySelector('.ignor-footer');
    const legalNav = footer?.querySelector('.ignor-footer__legal');
    if (!footer || !legalNav) return;

    const controls = Array.from(footer.querySelectorAll('button, a, [role="button"]'))
      .filter(element => element.textContent?.trim().toLowerCase() === 'cookie preferences');

    controls.forEach(control => {
      control.classList.add('ignor-cookie-preferences-control');
      if (control.parentElement !== legalNav) legalNav.appendChild(control);
    });
  }

  function initializeFooterPrivacyControls() {
    organizeFooterPrivacyControls();

    footerObserver?.disconnect();
    const footer = document.querySelector('.ignor-footer');
    if (!footer) return;

    footerObserver = new MutationObserver(() => organizeFooterPrivacyControls());
    footerObserver.observe(footer, { childList: true, subtree: true });
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(TOGGLE_SELECTOR);
    if (button) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      closeAllMenus({ focusPanel: false });
      setMenuOpen(button, willOpen, {
        focusPanel: willOpen,
        restoreFocus: !willOpen
      });
      return;
    }

    const menuLink = event.target.closest(`${PANEL_SELECTOR} a[href]`);
    if (menuLink) {
      const panel = menuLink.closest(PANEL_SELECTOR);
      const ownerButton = findButtonForPanel(panel);
      if (ownerButton) setMenuOpen(ownerButton, false, { focusPanel: false });
    }
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;

    const openButton = document.querySelector(`${TOGGLE_SELECTOR}[aria-expanded="true"]`);
    if (!openButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    setMenuOpen(openButton, false, { restoreFocus: true, focusPanel: false });
  }, true);

  window.addEventListener('resize', () => {
    if (window.innerWidth > DESKTOP_BREAKPOINT) {
      closeAllMenus({ focusPanel: false });
    }
  }, { passive: true });

  function initializeInterfaceFixes(root = document) {
    initializeMenus(root);
    initializeFooterPrivacyControls();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeInterfaceFixes());
  } else {
    initializeInterfaceFixes();
  }

  document.addEventListener('shopify:section:load', event => initializeInterfaceFixes(event.target));
})();
